import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X } from "lucide-react";

/**
 * Floating WhatsApp-style chat with looping typewriter conversation.
 */
const SCRIPT = [
  { who: "user", text: "Oi, quero saber sobre vocês" },
  {
    who: "maya",
    text:
      "Olá! Sou a Maria 👋 Posso te ajudar a entender como a IA pode transformar seu negócio. Qual é o seu segmento?",
  },
  { who: "user", text: "Tenho uma clínica" },
  {
    who: "maya",
    text:
      "Perfeito. Clínicas que automatizam o atendimento reduzem 87% do tempo de resposta. Quer ver como funciona na prática?",
  },
];

const TYPE_SPEED = 22; // ms per char
const HOLD_AFTER = 1400; // ms pause between messages
const LOOP_PAUSE = 2600; // ms before restarting

export const MayaChat = () => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState([]);
  const [typing, setTyping] = useState("");
  const [typingWho, setTypingWho] = useState(null);
  const scrollRef = useRef(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    const run = async () => {
      while (!cancelled.current) {
        setVisible([]);
        for (let i = 0; i < SCRIPT.length; i++) {
          if (cancelled.current) return;
          const msg = SCRIPT[i];
          setTypingWho(msg.who);
          setTyping("");
          for (let c = 1; c <= msg.text.length; c++) {
            if (cancelled.current) return;
            setTyping(msg.text.slice(0, c));
            // eslint-disable-next-line no-await-in-loop
            await wait(TYPE_SPEED);
          }
          // commit
          setVisible((v) => [...v, msg]);
          setTyping("");
          setTypingWho(null);
          // eslint-disable-next-line no-await-in-loop
          await wait(HOLD_AFTER);
        }
        // eslint-disable-next-line no-await-in-loop
        await wait(LOOP_PAUSE);
      }
    };
    run();
    return () => {
      cancelled.current = true;
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visible, typing]);

  return (
    <div
      className="fixed bottom-20 right-5 sm:bottom-24 sm:right-6 z-50"
      data-testid="maya-chat-widget"
    >
      {open ? (
        <div
          className="w-[320px] sm:w-[360px] rounded-2xl overflow-hidden bg-[#0E0E0E] sage-glow-soft border border-[rgba(197,212,181,0.18)]"
          data-cursor="hover"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(197,212,181,0.12)] bg-gradient-to-b from-[rgba(197,212,181,0.06)] to-transparent">
            <div className="flex items-center gap-2.5">
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#C5D4B5] pulse-dot" />
              <span className="text-[13px] text-[#F5F1E8] tracking-tight">
                Maria · IA ativa 24/7
              </span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-[#F5F1E8]/60 hover:text-[#C5D4B5] transition-colors"
              data-testid="maya-close-btn"
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="px-4 py-4 h-[280px] overflow-y-auto bg-[#0A0A0A] space-y-2.5"
            style={{
              backgroundImage:
                "radial-gradient(900px circle at 50% -10%, rgba(197,212,181,0.05), transparent 60%)",
            }}
          >
            {visible.map((m, i) => (
              <Bubble key={i} who={m.who} text={m.text} />
            ))}
            {typingWho && <Bubble who={typingWho} text={typing} typing />}
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="group relative h-14 w-14 rounded-full bg-[#C5D4B5] text-[#0A0A0A] flex items-center justify-center hover:scale-105 transition-transform sage-glow"
          data-testid="maya-open-btn"
          aria-label="Abrir chat Maria"
        >
          <MessageCircle size={22} strokeWidth={2.2} />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#C5D4B5] pulse-dot border-2 border-[#0A0A0A]" />
        </button>
      )}
    </div>
  );
};

const Bubble = ({ who, text, typing }) => {
  const isUser = who === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[78%] px-3 py-2 rounded-xl text-[13px] leading-snug ${
          isUser
            ? "bg-[#1a1a1a] text-[#F5F1E8] rounded-br-sm"
            : "bg-[rgba(197,212,181,0.12)] text-[#F5F1E8] rounded-bl-sm border border-[rgba(197,212,181,0.18)]"
        }`}
      >
        <span className={typing ? "caret" : ""}>{text}</span>
      </div>
    </div>
  );
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default MayaChat;
